import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Eye, Camera } from 'lucide-react';
import { getImageById, getImageByIdNoView } from '../api/apiClient';
import ImageCard from '../components/ImageCard';
import Seo from '../components/Seo';
import { Loader2 } from 'lucide-react';
import LikeButton from '../components/LikeButton';
import { useTags } from '../context/TagsContext';
import { useAuth } from '../context/AuthContext';
import CmsOrNotFound from './CmsOrNotFound';
import NotFoundPage from './NotFoundPage';
import { toTitleCase } from '../utils/format';
import { SITE_URL } from '../seoConfig';

// Use window object to persist viewed images across HMR (hot module replacement)
if (!window.__viewedImages) {
  window.__viewedImages = new Set();
}

const PhotoDetailPage = () => {
  const params = useParams();
  const { user: currentUser } = useAuth();
  const { resolveTagSlug } = useTags();
  const [image, setImage] = useState(null);
  const [photographer, setPhotographer] = useState(null);
  const [relatedImages, setRelatedImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Extract imageId from params (handles both /photos/:imageId and /photos/:imageId-:alias)
  const getImageIdFromParams = () => {
    // If we have imageId directly
    if (params.imageId) {
      // Check if it contains a dash (SEO URL format: "1-sea-pool...")
      const match = params.imageId.match(/^(\d+)-/);
      if (match) {
        return parseInt(match[1]);
      }
      return parseInt(params.imageId);
    }
    return null;
  };

  const imageId = getImageIdFromParams();

  // Scroll to top whenever a new photo page is opened
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [imageId]);

  useEffect(() => {
    let isMounted = true;
    
    const fetchImage = async () => {
      if (!imageId || Number.isNaN(imageId)) {
        if (isMounted) setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setNotFound(false);
        let data;
        
        // Synchronous check: has this image been viewed already?
        // This prevents double-counting from React StrictMode re-renders
        const alreadyViewed = window.__viewedImages.has(imageId);
        
        if (!alreadyViewed) {
          // Mark as viewed BEFORE making the API call (synchronous)
          window.__viewedImages.add(imageId);
          // First view - use endpoint that increments views
          data = await getImageById(imageId);
        } else {
          // Already viewed - use endpoint that doesn't increment
          data = await getImageByIdNoView(imageId);
        }
        
        if (isMounted) {
          // Strict canonical URL: a photo lives at exactly one URL
          // (/photos/{id}-{alias}). Any other slug for this id (typo, missing or
          // wrong alias, id-only) is treated as a page that does not exist → 404.
          // No redirects: each link has one unique URL or it's a 404.
          const canonicalSlug = `${data.id}-${data.alias}`;
          if (params.imageId !== canonicalSlug) {
            setNotFound(true);
            return;
          }
          setImage(data);
          const formattedPhotog = data.photographer
            ? { ...data.photographer, user_display_name: toTitleCase(data.photographer.user_display_name) }
            : data.photographer;
          setPhotographer(formattedPhotog);
          setRelatedImages(
            (data.related_photos || []).map(r => ({
              ...r,
              photographer: r.photographer
                ? { ...r.photographer, user_display_name: toTitleCase(r.photographer.user_display_name) }
                : r.photographer,
            }))
          );
        }
      } catch (err) {
        console.error('Error fetching image:', err);
        // On error, remove from viewed so user can retry
        window.__viewedImages.delete(imageId);
        if (isMounted) setNotFound(true);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchImage();
    
    return () => {
      isMounted = false;
    };
  }, [imageId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
      </div>
    );
  }

  // If the URL doesn't carry a numeric image id (e.g. /photos/tags),
  // hand off to the CMS-or-404 resolver so admin-created pages still load.
  if (!imageId || Number.isNaN(imageId)) {
    return <CmsOrNotFound />;
  }

  if (notFound || !image || !photographer) {
    return <NotFoundPage />;
  }

  return (
    <div className="min-h-screen bg-white">
      {image.published === 1 && (() => {
        const canonicalUrl = `${SITE_URL}/photos/${image.id}-${image.alias}`;
        const seoTitle = image.seo_title || image.title;
        const metaDescription = image.meta_description || '';

        // ImageObject — for the MAIN photo only. Includes licensing/copyright
        // signals (not free-to-use). No structured data for related photos.
        const photographerName = photographer.user_display_name;
        const imageObjectLd = {
          '@context': 'https://schema.org',
          '@type': 'ImageObject',
          name: seoTitle,
          description: metaDescription,
          contentUrl: image.image_urls?.full,
          thumbnailUrl: image.image_urls?.thumbnail,
          url: canonicalUrl,
          creator: { '@type': 'Person', name: photographerName },
          creditText: `Photo by ${photographerName} on Explora`,
          copyrightNotice: `© ${photographerName}. All rights reserved.`,
          copyrightHolder: { '@type': 'Person', name: photographerName },
          license: `${SITE_URL}/photo-rights`,
          acquireLicensePage: `${SITE_URL}/contact`,
          width: 1200,
          ...(image.date_published ? { datePublished: image.date_published } : {}),
          ...((image.tags && image.tags.length) ? { keywords: image.tags.join(', ') } : {}),
        };

        // BreadcrumbList: Home → Photos → {Photo}
        const breadcrumbLd = {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
            { '@type': 'ListItem', position: 2, name: 'Photos', item: `${SITE_URL}/photos` },
            { '@type': 'ListItem', position: 3, name: seoTitle, item: canonicalUrl },
          ],
        };

        // Related photos are dynamic and may belong to other photographers, so
        // they remain plain visible links/cards — no structured data for them.
        const jsonLd = [imageObjectLd, breadcrumbLd];

        // All SEO output (title, meta, OG, Twitter, JSON-LD) goes through <Seo>
        // so react-helmet-async places it in <head> (not the body).
        return (
          <Seo
            title={seoTitle}
            description={metaDescription}
            canonical={canonicalUrl}
            image={image.image_urls?.full}
            imageAlt={image.image_alt || image.title}
            type="article"
            jsonLd={jsonLd}
          />
        );
      })()}

      {/* Breadcrumb — Home › Photos › {title}. On mobile, "Home ›" is hidden
          (shows "Photos › Title") and the last item truncates to protect layout. */}
      <div className="px-4 sm:px-6 lg:px-8 py-4 max-w-full md:max-w-[70%] mx-auto">
        <nav
          className="breadcrumb flex items-center gap-1.5 text-sm text-gray-500 min-w-0"
          aria-label="Breadcrumb"
          data-testid="photo-breadcrumb"
        >
          <Link to="/" className="hidden sm:inline hover:text-gray-900 transition-colors whitespace-nowrap">Home</Link>
          <span className="hidden sm:inline text-gray-300 flex-shrink-0" aria-hidden="true">›</span>
          <Link to="/photos" className="hover:text-gray-900 transition-colors whitespace-nowrap flex-shrink-0">Photos</Link>
          <span className="text-gray-300 flex-shrink-0" aria-hidden="true">›</span>
          <span
            className="text-gray-700 font-medium truncate min-w-0"
            aria-current="page"
            title={image.seo_title || image.title}
          >
            {image.seo_title || image.title}
          </span>
        </nav>
      </div>

      {/* Image Section */}
      <div className="px-0 md:px-4 sm:px-6 lg:px-8 max-w-full md:max-w-[70%] mx-auto pb-8">
        {/* Photographer Info & Actions */}
        <div className="flex items-center justify-between mb-6 px-4 md:px-0">
          <Link
            to={photographer.user_url}
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
          >
            <img
              src={photographer.user_image_url || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(photographer.user_display_name)}
              alt={`${photographer.user_display_name} Photographer on Explora Mauritius`}
              className="w-12 h-12 rounded-full object-cover"
            />
            <div>
              <h3 className="font-semibold text-gray-900">{photographer.user_display_name}</h3>
              <p className="text-sm text-gray-500">@{photographer.user_display_name.toLowerCase().replace(/\s+/g, '')}</p>
            </div>
          </Link>

          <div className="flex items-center space-x-2">
          </div>
        </div>

        {/* Main Image */}
        <div className="overflow-hidden md:rounded-lg bg-gray-100">
          <img
            src={image.image_urls.full}
            alt={image.image_alt || image.title}
            className="w-full h-auto object-contain max-h-[80vh]"
            {...(image.height_1200 ? { width: 1200, height: image.height_1200 } : {})}
          />
        </div>

        {/* Image Details */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-8 px-4 md:px-0">
          {/* Left Column */}
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{image.title}</h1>
            <div
              className="photo-description text-gray-600 mb-4 prose max-w-none"
              dangerouslySetInnerHTML={{ __html: image.description }}
            />
            
            <div className="flex items-center space-x-4 text-gray-600 mb-6">
              <div className="flex items-center space-x-1">
                <Eye className="w-5 h-5" />
                <span>{image.views?.toLocaleString() || 0} views</span>
              </div>
              {!(currentUser && currentUser.user_id === image.user_id) && (
                <>
                  <span className="text-gray-300">•</span>
                  <LikeButton
                    imageId={image.id}
                    initialLikes={image.likes || 0}
                    size="default"
                    showLabel
                  />
                </>
              )}
              {image.date_published && (
                <>
                  <span className="text-gray-300">•</span>
                  <span>
                    {new Date(image.date_published).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </>
              )}
            </div>

            {/* Tags */}
            {image.tags && image.tags.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {image.tags.map((tag, idx) => {
                    const slug = resolveTagSlug(tag);
                    if (!slug) {
                      return (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-gray-100 text-gray-800 text-sm rounded-md"
                        >
                          {tag}
                        </span>
                      );
                    }
                    return (
                      <a
                        key={idx}
                        href={`/photos-hd/${slug}`}
                        className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm rounded-md transition-colors"
                      >
                        {tag}
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Camera Info */}
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Camera className="w-5 h-5 text-gray-700" />
              <h3 className="font-semibold text-gray-900">Camera Details</h3>
            </div>

            <div className="space-y-3 text-sm">
              {(() => {
                // Treat placeholder strings as "missing" so we render a clean dash.
                const isMissing = (v) => {
                  if (v === null || v === undefined) return true;
                  const s = String(v).trim().toLowerCase();
                  return s === '' || s === 'nan' || s === 'n/a' || s === 'none' || s === 'null';
                };
                const show = (v) => isMissing(v) ? '-' : v;
                const rows = [
                  ['Camera', image.cam_model],
                  ['Lens', image.cam_lens],
                  ['Aperture', image.cam_aperture],
                  ['Shutter Speed', image.cam_exp],
                  ['ISO', image.cam_iso],
                  ['Focal Length', image.cam_foc],
                ];
                return rows.map(([label, val]) => (
                  <div key={label} className="flex justify-between">
                    <span className="text-gray-600">{label}</span>
                    <span className="font-medium text-gray-900">{show(val)}</span>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Related Images */}
      {relatedImages.length > 0 && (() => {
        const mainTag = image.tags && image.tags.length > 0 ? image.tags[0] : '';
        const mainTagTitle = mainTag; // tags are already properly cased in DB
        const mainTagSlug = mainTag ? resolveTagSlug(mainTag) : null;
        return (
          <div className="border-t border-gray-200 py-12">
            <div className="px-4 sm:px-6 lg:px-8 max-w-full md:max-w-[70%] mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" data-testid="related-photos-heading">
                More Mauritius photos tagged {mainTagTitle}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
                {relatedImages.slice(0, 12).map((rel) => (
                  <div key={rel.id} data-testid={`related-photo-${rel.id}`}>
                    <ImageCard
                      image={rel}
                      showPhotographer={true}
                      showTags={false}
                      photographerClickable={false}
                      tagsClickable={false}
                      showLike={false}
                      cropToSquare={true}
                    />
                  </div>
                ))}
              </div>
              {mainTagSlug && (
                <div className="mt-8 flex justify-center">
                  <a
                    href={`/photos-hd/${mainTagSlug}`}
                    data-testid="related-photos-cta"
                    className="inline-block px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-base rounded transition-colors"
                  >
                    View All {mainTagTitle} Photos
                  </a>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default PhotoDetailPage;
